// Lộ trình nâng cấp (docs/KE_HOACH_NANG_CAP_DON_VI.md §3.7, §3.12) — Shop / CTV xem chỉ tiêu nhà phân phối đặt, tiến độ
// tháng này, kết quả các tháng đã kết thúc; đủ điều kiện thì gửi yêu cầu lên CTV / Đại lý. Cấp trên mới duyệt (Đại lý
// khi ở lại nhánh Đại lý; còn lại nhà phân phối). Duyệt xong giao diện tự chuyển sang chức năng mới.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowUpCircle, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { del, get, post } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { money } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/ui/page-header'
import { Section } from '@/components/ui/section'
import { LoadError } from '@/components/ui/data-notice'
import { IndexBadge } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ProgressBar } from '@/components/commissions/parts'
import { errorMessage } from '@/components/price-lists/types'
import { TO_LABEL, TYPE_LABEL, monthLabel, type ProgressItem, type UpgradeDirection, type UpgradeProgress, type UpgradeRequest } from '@/components/upgrade/types'

const fmt = (p: ProgressItem, v: number) => (p.key === 'goodsValue' ? money(v) : v.toLocaleString('vi-VN'))
const UNIT: Record<ProgressItem['key'], string> = { deliveredOrders: 'đơn', goodsValue: '', activeChildren: 'Shop' }

export default function UpgradePage() {
  const q = useQuery({ queryKey: ['upgrade', 'progress'], queryFn: () => get<UpgradeProgress>('/upgrade/progress') })
  const shop = getAuthState().shops.find((s) => s.shopId === getAuthState().activeShopId)
  const isOwner = shop?.role === 'owner'
  const d = q.data
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader title="Nâng cấp" />
      {q.isError ? (
        <LoadError onRetry={() => q.refetch()} />
      ) : !d ? (
        <p className="p-8 text-center text-sm text-muted">Đang tải…</p>
      ) : (
        <>
          {d.pending && <PendingCard req={d.pending} canCancel={isOwner} />}
          {d.directions.length === 0 ? (
            <Section>
              <p className="p-4 text-sm text-inkSoft">
                {d.upgradeTargets.length === 0
                  ? `${TYPE_LABEL[d.tenantType] ?? d.tenantType} là cấp cao nhất trong mạng lưới.`
                  : 'Chưa có chỉ tiêu nâng cấp.'}
              </p>
            </Section>
          ) : (
            d.directions.map((x) => <DirectionCard key={x.toType} dir={x} canRequest={isOwner && !d.pending} />)
          )}
          {d.history.length > 0 && <HistoryCard rows={d.history} />}
        </>
      )}
    </div>
  )
}

function DirectionCard({ dir, canRequest }: { dir: UpgradeDirection; canRequest: boolean }) {
  const [asking, setAsking] = useState(false)
  const eligible = dir.eligibility?.status === 'eligible'
  const t = dir.target
  return (
    <Section
      title={
        <span className="flex items-center gap-2">
          Lên {TO_LABEL[dir.toType]}
          {eligible && <span className="rounded-full bg-ok/12 px-2 py-0.5 text-xs font-semibold text-ok">Đủ điều kiện</span>}
          {dir.eligibility?.status === 'requested' && <span className="rounded-full bg-info/12 px-2 py-0.5 text-xs font-semibold text-info">Đã gửi yêu cầu</span>}
        </span>
      }
      action={
        eligible && canRequest ? (
          <Button size="sm" className="gap-1.5" onClick={() => setAsking(true)}>
            <ArrowUpCircle className="size-4" /> Gửi yêu cầu
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4 p-4 text-sm">
        <p className="text-inkSoft">
          Chỉ tiêu: đạt {t.consecutiveMonths > 1 ? `${t.consecutiveMonths} tháng liền nhau` : 'một tháng'}
          {t.minAccountAgeDays ? `, đơn vị hoạt động từ ${t.minAccountAgeDays} ngày` : ''}.
          {t.note ? ` ${t.note}` : ''}
        </p>
        <div className="space-y-3">
          <p className="text-xs font-semibold text-ink">Tháng này ({monthLabel(dir.currentMonth.month)})</p>
          {dir.currentMonth.progress.map((p) => (
            <div key={p.key} className="space-y-1">
              <div className="flex justify-between gap-3 text-xs">
                <span className="text-inkSoft">{p.label}</span>
                <span className={cn('tabular-nums', p.ok ? 'font-semibold text-ok' : 'text-ink')}>
                  {fmt(p, p.value)} / {fmt(p, p.target)} {UNIT[p.key]}
                </span>
              </div>
              <ProgressBar value={p.pct / 100} label={p.label} className="h-1.5" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {dir.closedMonths.map((m) => (
            <span key={m.month} className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs', m.ok ? 'bg-ok/10 text-ok' : 'bg-paperSoft text-inkSoft')}>
              {m.ok ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />} Tháng {monthLabel(m.month)}
            </span>
          ))}
          {!dir.ageOk && <span className="rounded-full bg-paperSoft px-2.5 py-1 text-xs text-inkSoft">Chưa đủ thời gian hoạt động</span>}
        </div>
        {dir.meetsNow && !dir.eligibility && <p className="text-xs text-inkSoft">Đã đạt các tháng gần nhất.</p>}
        {eligible && dir.eligibility && <p className="text-xs text-inkSoft">Gửi yêu cầu trước {new Date(dir.eligibility.expiresAt).toLocaleDateString('vi-VN')}.</p>}
      </div>
      {asking && <RequestDialog dir={dir} onClose={() => setAsking(false)} />}
    </Section>
  )
}

function RequestDialog({ dir, onClose }: { dir: UpgradeDirection; onClose: () => void }) {
  const qc = useQueryClient()
  const [message, setMessage] = useState('')
  const send = useMutation({
    mutationFn: () => post('/upgrade/requests', { toType: dir.toType, message: message.trim() || null }),
    onSuccess: () => {
      toast.success('Đã gửi yêu cầu')
      qc.invalidateQueries({ queryKey: ['upgrade'] })
      onClose()
    },
    onError: (e) => toast.error(errorMessage(e, 'Không gửi được yêu cầu')),
  })
  const p = dir.plan
  return (
    <Dialog open onOpenChange={(o) => !o && !send.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader className="pr-8">
          <DialogTitle>Xin lên {TO_LABEL[dir.toType]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <ul className="list-disc space-y-1 pl-5 text-ink">
            <li>{p.newParent.id ? `Bạn thành ${TO_LABEL[dir.toType]} dưới ${p.newParent.name}.` : `Bạn thành ${TO_LABEL[dir.toType]} trực thuộc ${p.newParent.name}.`}</li>
            {p.leaves.length > 0 && <li>Rời nhánh {p.leaves.map((l) => `${TYPE_LABEL[l.type] ?? l.type} ${l.name}`).join(', ')}.</li>}
          </ul>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Lời nhắn (không bắt buộc)"
            className="w-full rounded-lg border border-rule bg-paper px-3 py-2 text-sm"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={send.isPending}>Hủy</Button>
          <Button onClick={() => send.mutate()} disabled={send.isPending}>{send.isPending ? 'Đang gửi…' : 'Gửi yêu cầu'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PendingCard({ req, canCancel }: { req: UpgradeRequest; canCancel: boolean }) {
  const qc = useQueryClient()
  const cancel = useMutation({
    mutationFn: () => del(`/upgrade/requests/${req.id}`),
    onSuccess: () => {
      toast.success('Đã hủy yêu cầu')
      qc.invalidateQueries({ queryKey: ['upgrade'] })
    },
    onError: (e) => toast.error(errorMessage(e, 'Không hủy được')),
  })
  return (
    <Section>
      <div className="flex flex-wrap items-start gap-3 p-4 text-sm">
        <Clock className="mt-0.5 size-5 shrink-0 text-info" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-ink">Yêu cầu lên {TO_LABEL[req.data.toType]} đang chờ duyệt</p>
          <p className="text-inkSoft">
            {req.data.approver === 'agency' ? `${req.newParent.name} duyệt` : 'Nhà phân phối duyệt'} · gửi {new Date(req.createdAt).toLocaleDateString('vi-VN')}
          </p>
        </div>
        {canCancel && (
          <Button size="sm" variant="outline" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            Hủy yêu cầu
          </Button>
        )}
      </div>
    </Section>
  )
}

const RESULT: Record<string, string> = { approved: 'Đã duyệt', rejected: 'Không được duyệt', cancelled: 'Đã hủy' }

function HistoryCard({ rows }: { rows: UpgradeRequest[] }) {
  return (
    <Section title="Yêu cầu trước">
      <ul className="divide-y divide-rule px-4 text-sm">
        {rows.map((r, i) => (
          <li key={r.id} className="flex items-start gap-2 py-2">
            <IndexBadge n={i + 1} />
            <div className="min-w-0 flex-1">
              <p className="text-ink">
                Lên {TO_LABEL[r.data.toType]} · <span className={r.status === 'approved' ? 'text-ok' : r.status === 'rejected' ? 'text-danger' : 'text-inkSoft'}>{RESULT[r.status] ?? r.status}</span>
              </p>
              <p className="text-xs text-inkSoft">
                {new Date(r.createdAt).toLocaleDateString('vi-VN')}
                {r.reviewNote ? ` · ${r.reviewNote}` : ''}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  )
}
